/**
 * Pure helpers for the edit-realty saved baseline / dirty contract.
 * Keeps comparison deterministic without unstable JSON.stringify of Sets.
 */

const EMPTY_STRING_KEYS_HINT = new Set([
  "title",
  "description_public",
  "description_private",
  "property_type",
  "deal_type",
  "price",
  "currency",
  "video_url",
  "local_area",
  "manual_address_object",
  "building_number",
  "legacyUnresolvedAddress",
  "namedObjectMode",
  "latitude",
  "longitude",
  "walls",
  "heating",
  "condition",
  "furniture",
  "residential_complex",
  "suitable_for",
  "purpose",
  "cooperation_type",
  "cooperation_value",
  "cooperation_currency",
  "cooperation_terms",
]);

const normalizeScalar = (value) => {
  if (value === undefined || value === null) return "";
  if (typeof value === "boolean") return value;
  if (typeof value === "number") {
    return Number.isFinite(value) ? String(value) : "";
  }
  return String(value);
};

const normalizeObject = (source) => {
  const out = {};
  if (!source || typeof source !== "object") return out;
  const keys = Object.keys(source).sort();
  for (const key of keys) {
    if (key === "selectedRegion" || key === "selectedSettlement") {
      const unit = source[key];
      out[key] = unit && unit.code ? String(unit.code) : "";
      continue;
    }
    if (key === "address_object") {
      const addressObject = source[key];
      out[key] = addressObject && addressObject.id != null
        ? String(addressObject.id)
        : "";
      continue;
    }
    if (
      key === "canonicalSelectionDirty"
      || key === "structuredIdentityDirty"
      || key === "legacyAddressResolved"
      || key === "legacyCompatibilityCopy"
    ) {
      out[key] = Boolean(source[key]);
      continue;
    }
    const value = source[key];
    if (typeof value === "boolean") {
      out[key] = value;
    } else if (
      value === null
      || value === undefined
      || EMPTY_STRING_KEYS_HINT.has(key)
      || typeof value === "string"
      || typeof value === "number"
    ) {
      out[key] = normalizeScalar(value);
    } else if (typeof value === "object") {
      out[key] = normalizeObject(value);
    } else {
      out[key] = normalizeScalar(value);
    }
  }
  return out;
};

export const normalizeFeatureIds = (featureIds) => {
  const ids = [];
  if (featureIds && typeof featureIds[Symbol.iterator] === "function") {
    for (const id of featureIds) {
      if (id === null || id === undefined || id === "") continue;
      ids.push(String(id));
    }
  }
  ids.sort();
  return ids;
};

export const buildEditBaselineSnapshot = (form, featureIds) => {
  if (!form) return null;
  return {
    core: normalizeObject(form.core || {}),
    location: normalizeObject(form.location || {}),
    residential: normalizeObject(form.residential || {}),
    land: normalizeObject(form.land || {}),
    commercial: normalizeObject(form.commercial || {}),
    featureIds: normalizeFeatureIds(featureIds),
  };
};

const snapshotsEqual = (a, b) => {
  if (a === b) return true;
  if (!a || !b) return false;
  return JSON.stringify(a) === JSON.stringify(b);
};

export const isEditFormDirty = (baseline, form, featureIds) => {
  if (!baseline || !form) return false;
  const current = buildEditBaselineSnapshot(form, featureIds);
  return !snapshotsEqual(baseline, current);
};
